import { useState } from 'react';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'src/@/components/ui/dialog';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Textarea } from 'src/@/components/ui/textarea';
import QuestionRow from './QuestionRow';
import { CATALOGO } from './catalog';

function AddCustomDialog({ open, onOpenChange, onSubmit }) {
    const [titulo, setTitulo] = useState('');
    const [descricao, setDescricao] = useState('');

    const reset = () => {
        setTitulo('');
        setDescricao('');
    };

    const handleClose = (next) => {
        if (!next) reset();
        onOpenChange(next);
    };

    const handleSubmit = () => {
        const t = titulo.trim();
        if (!t) return;
        onSubmit({ titulo: t, descricao: descricao.trim() });
        handleClose(false);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Nova pergunta customizada</DialogTitle>
                    <DialogDescription>
                        Crie uma pergunta específica para sua operação. A Lais fará no melhor momento da conversa.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-4 py-2">
                    <div className="flex flex-col gap-2">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Título</Label>
                        <Input
                            value={titulo}
                            onChange={(e) => setTitulo(e.target.value)}
                            placeholder="Ex.: Tem preferência por andar alto?"
                            className="rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20"
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Descrição</Label>
                        <Textarea
                            value={descricao}
                            onChange={(e) => setDescricao(e.target.value)}
                            placeholder="Explique o motivo ou contexto da pergunta."
                            rows={3}
                            className="rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20"
                        />
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={() => handleClose(false)} className="rounded-xl">
                        Cancelar
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!titulo.trim()}
                        className="rounded-xl bg-[#7C3AED] hover:bg-[#6d28d9] text-white"
                    >
                        Adicionar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function PerguntasGlobaisCard({
    perguntas,
    onChangePerguntas,
    getEssencialDisabled,
    getQualifDisabled,
}) {
    const [showAddDialog, setShowAddDialog] = useState(false);
    const getPergunta = (id) => perguntas.find((p) => p.id === id);
    const customList = perguntas.filter((p) => p.custom);

    const updateState = (id, patch, extras = {}) => {
        const existing = getPergunta(id);
        // `extras` carries catalog-derived metadata (e.g. spinField) that must
        // be applied on every update, not only on the first creation. This
        // backfills old entries that were saved before we introduced spinField
        // without clobbering user-set fields like `script` (those come via patch).
        const next = { ...(existing || { id }), ...extras, ...patch };

        if (patch.ativa === false) next.qualificatoria = false;
        if (patch.qualificatoria === true && !next.ativa) return;

        if (existing) {
            onChangePerguntas(perguntas.map((p) => (p.id === id ? next : p)));
        } else {
            onChangePerguntas([...perguntas, next]);
        }
    };

    const addCustom = ({ titulo, descricao }) => {
        const id = `custom_${Date.now()}`;
        onChangePerguntas([
            ...perguntas,
            { id, custom: true, titulo, descricao, ativa: false, qualificatoria: false },
        ]);
    };

    const removeCustom = (id) => {
        onChangePerguntas(perguntas.filter((p) => p.id !== id));
    };

    return (
        <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader>
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                            <Iconify icon="mdi:earth" width={20} className="text-violet-600" />
                        </div>
                        <div>
                            <CardTitle className="text-base">Perguntas globais</CardTitle>
                            <CardDescription>
                                Coletadas em todo atendimento, independente do tipo de imóvel.
                            </CardDescription>
                        </div>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAddDialog(true)}
                        className="rounded-xl border-gray-200 hover:border-[#7C3AED] hover:text-[#7C3AED]"
                    >
                        <Iconify icon="eva:plus-outline" width={16} className="mr-1" />
                        Adicionar
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                <div className="flex flex-col divide-y divide-gray-100">
                    {CATALOGO.map((item) => {
                        const state = getPergunta(item.id);
                        // Itens sem spinField nao tem extracao no agente — marcar/qualificar
                        // nao tem efeito (buildEssenciaisFromConfig em identity.js:848 e
                        // contarQualificatoriasRespondidas em crmParceiro.service.js:76 ambos
                        // filtram por spinField). Travamos os switches pra nao iludir o admin.
                        const naoExtraivel = !item.spinField;
                        return (
                            <QuestionRow
                                key={item.id}
                                icon={item.icon}
                                titulo={item.titulo}
                                descricao={item.descricao}
                                state={state}
                                essencialDisabled={naoExtraivel || getEssencialDisabled(state)}
                                qualificatoriaDisabled={naoExtraivel || getQualifDisabled(state)}
                                spinField={item.spinField}
                                defaultScript={item.defaultScript}
                                naoExtraivel={naoExtraivel}
                                onChange={(patch) => updateState(item.id, patch, { spinField: item.spinField })}
                            />
                        );
                    })}
                    {customList.map((p) => (
                        <QuestionRow
                            key={p.id}
                            icon="mdi:comment-question-outline"
                            titulo={p.titulo}
                            descricao={p.descricao}
                            state={p}
                            essencialDisabled={getEssencialDisabled(p)}
                            qualificatoriaDisabled={getQualifDisabled(p)}
                            onChange={(patch) => updateState(p.id, patch, { custom: true, titulo: p.titulo, descricao: p.descricao })}
                            onDelete={() => removeCustom(p.id)}
                        />
                    ))}
                </div>
            </CardContent>

            <AddCustomDialog open={showAddDialog} onOpenChange={setShowAddDialog} onSubmit={addCustom} />
        </Card>
    );
}
